import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Timer, Loader2, Trophy, TrendingUp, TrendingDown, Check, Coins, Zap, ChevronDown, Camera } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { MathText } from "../components/MathText";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Simulation() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const tr = useT();
  const [sim, setSim] = useState(null);
  const [answers, setAnswers] = useState({});
  const [time, setTime] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.post(`/exams/${examId}/simulation/start`).then((r) => { setSim(r.data); setTime(r.data.duration); })
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); navigate(`/exam/${examId}`); });
  }, [examId, navigate]);

  const submit = useRef(null);
  submit.current = async () => {
    if (submitting || result) return;
    setSubmitting(true);
    const payload = { answers: sim.questions.map((q) => ({ idx: q.idx, choice: answers[q.idx]?.choice ?? null, text: answers[q.idx]?.text ?? null, image: answers[q.idx]?.image ?? null })) };
    try {
      const { data } = await api.post(`/simulation/${sim.sim_id}/submit`, payload);
      setResult(data); setUser(data.user);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (!sim || result) return;
    if (time <= 0) { toast.info(tr("Tempo scaduto!")); submit.current(); return; }
    const t = setTimeout(() => setTime((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [time, sim, result]);

  if (!sim) return (
    <div className="space-bg min-h-screen flex flex-col items-center justify-center gap-3 text-white/70 px-6 text-center">
      <Loader2 size={40} className="animate-spin" style={{ color: "var(--pink)" }} />
      {tr("Preparo la simulazione d'esame... l'AI crea esercizi nuovi ogni volta.")}
    </div>
  );

  if (result) {
    const scoreColor = result.overall >= 70 ? "var(--success)" : result.overall >= 50 ? "var(--gold)" : "var(--danger)";
    return (
      <div className="space-bg min-h-screen pb-20">
        <div className="max-w-2xl mx-auto px-4 pt-8">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-6 text-center mb-6" data-testid="sim-result-card">
            <Trophy size={40} style={{ color: scoreColor }} className="mx-auto mb-2" />
            <h1 className="font-heading font-black text-3xl tracking-tight">{tr("Voto stimato")}</h1>
            <div className="font-num font-bold text-6xl my-2" style={{ color: scoreColor }}>{result.voto}<span className="text-2xl text-white/40">/30</span></div>
            <p className="text-white/60">{tr("Preparazione complessiva: ")}{result.overall}%</p>
            <div className="flex justify-center gap-6 font-num mt-4">
              <span className="flex items-center gap-1"><Zap size={18} style={{ color: "var(--cyan)" }} /> +{result.xp_earned} XP</span>
            </div>
          </motion.div>

          {result.targeted_review > 0 && (
            <button onClick={() => navigate("/review")} className="w-full glass rounded-2xl p-4 mb-6 flex items-center gap-3 bouncy" data-testid="sim-targeted-review-btn">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,0,127,0.15)" }}>
                <TrendingDown size={20} style={{ color: "var(--pink)" }} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-heading font-bold text-sm">{tr("Ripasso mirato pronto")}</p>
                <p className="text-xs text-white/60">{result.targeted_review} domande dei tuoi punti deboli aggiunte al Ripasso</p>
              </div>
            </button>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="glass rounded-2xl p-4" data-testid="sim-strengths">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-2" style={{ color: "var(--success)" }}><TrendingUp size={18} /> {tr("Punti forti")}</h3>
              {result.strengths.length ? result.strengths.map((s, i) => <p key={i} className="text-sm text-white/70">• {s}</p>) : <p className="text-sm text-white/40">{tr("Nessuno ancora")}</p>}
            </div>
            <div className="glass rounded-2xl p-4" data-testid="sim-weaknesses">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-2" style={{ color: "var(--danger)" }}><TrendingDown size={18} /> {tr("Da migliorare")}</h3>
              {result.weaknesses.length ? result.weaknesses.map((s, i) => <p key={i} className="text-sm text-white/70">• {s}</p>) : <p className="text-sm text-white/40">{tr("Nessuno!")}</p>}
            </div>
          </div>

          <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{tr("Punteggio per argomento")}</h3>
          <div className="space-y-2 mb-8">
            {result.per_topic.map((t, i) => {
              const c = t.score >= 70 ? "var(--success)" : t.score >= 50 ? "var(--gold)" : "var(--danger)";
              return (
                <div key={i} className="glass rounded-2xl p-3" data-testid={`sim-topic-${i}`}>
                  <div className="flex justify-between text-sm mb-1"><span>{t.topic}</span><span className="font-num" style={{ color: c }}>{t.score}%</span></div>
                  <div className="h-2 rounded-full bg-black/40 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${t.score}%`, background: c }} /></div>
                </div>
              );
            })}
          </div>

          <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{tr("Revisione")}</h3>
          <div className="space-y-2 mb-6">
            {result.details.map((d, i) => (
              <div key={i} className="glass rounded-2xl overflow-hidden" data-testid={`sim-detail-${i}`}>
                <button onClick={() => setOpenDetail(openDetail === i ? null : i)} className="w-full flex items-center gap-2 p-3 text-left">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-num shrink-0"
                    style={{ background: (d.type === "mcq" ? d.correct : d.score >= 60) ? "rgba(0,255,127,0.2)" : "rgba(255,51,102,0.2)", color: (d.type === "mcq" ? d.correct : d.score >= 60) ? "var(--success)" : "var(--danger)" }}>
                    {d.type === "mcq" ? (d.correct ? "✓" : "✗") : `${d.score}`}
                  </span>
                  <span className="flex-1 text-sm text-white/80 line-clamp-1"><MathText>{d.q}</MathText></span>
                  <ChevronDown size={16} className={`text-white/40 transition-transform ${openDetail === i ? "rotate-180" : ""}`} />
                </button>
                {openDetail === i && (
                  <div className="px-3 pb-3 text-sm text-white/70 space-y-2 border-t border-white/10 pt-2">
                    <div><MathText>{d.q}</MathText></div>
                    {d.type === "mcq" ? (
                      <>
                        <p className="text-xs" style={{ color: "var(--success)" }}>{tr("Risposta corretta: ")}<MathText>{d.options[d.answer]}</MathText></p>
                        {d.explanation && <p className="text-xs text-white/60"><MathText>{d.explanation}</MathText></p>}
                      </>
                    ) : (
                      <>
                        {d.feedback && <p className="text-xs" style={{ color: "var(--gold)" }}>{tr("Feedback: ")}{d.feedback}</p>}
                        <p className="text-xs text-white/50">{tr("Risposta modello:")}</p>
                        <div className="text-xs text-white/70"><MathText>{d.model_answer}</MathText></div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={() => navigate(`/exam/${examId}`)} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="sim-done-btn">{tr("Torna all'esame")}</button>
        </div>
      </div>
    );
  }

  const answered = Object.keys(answers).length;
  return (
    <div className="space-bg min-h-screen pb-24">
      <header className="glass sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/exam/${examId}`)} className="text-white/60 bouncy"><X size={22} /></button>
        <span className="font-heading font-bold text-sm">{tr("Simulazione · ")}{answered}/{sim.questions.length}</span>
        <span className={`flex items-center gap-1 font-num font-bold ${time <= 60 ? "text-red-400" : "text-white/80"}`} data-testid="sim-timer">
          <Timer size={16} /> {fmt(time)}
        </span>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-5 space-y-4">
        <p className="text-white/50 text-sm">{tr("Rispondi a tutte le domande. Le domande aperte verranno valutate dall'AI. In bocca al lupo! 🎓")}</p>
        {sim.questions.map((q, qi) => (
          <div key={q.idx} className="glass rounded-3xl p-5" data-testid={`sim-q-${q.idx}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs px-2 py-0.5 rounded-full font-heading" style={{ background: q.type === "open" ? "rgba(255,0,127,0.2)" : "rgba(0,229,255,0.15)", color: q.type === "open" ? "var(--pink)" : "var(--cyan)" }}>
                {q.type === "open" ? tr("Aperta") : tr("Scelta multipla")}
              </span>
              <span className="text-xs text-white/40">{q.topic}</span>
            </div>
            <p className="font-heading font-bold tracking-tight mb-3"><span className="text-white/40">{qi + 1}. </span><MathText>{q.q}</MathText></p>
            {q.type === "mcq" ? (
              <div className="space-y-2">
                {q.o.map((opt, oi) => {
                  const sel = answers[q.idx]?.choice === oi;
                  return (
                    <button key={oi} onClick={() => setAnswers({ ...answers, [q.idx]: { choice: oi } })}
                      className="w-full text-left px-4 py-3 rounded-2xl font-body text-sm glass"
                      style={{ border: sel ? "2px solid var(--cyan)" : "1px solid rgba(255,255,255,0.12)", background: sel ? "rgba(0,229,255,0.12)" : undefined }}
                      data-testid={`sim-q-${q.idx}-opt-${oi}`}>
                      <MathText>{opt}</MathText>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                <textarea rows={4} value={answers[q.idx]?.text || ""} onChange={(e) => setAnswers({ ...answers, [q.idx]: { ...answers[q.idx], text: e.target.value } })}
                  placeholder={tr("Scrivi qui la tua risposta / svolgimento... oppure allega una foto")}
                  className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none text-sm placeholder-white/40 resize-none" data-testid={`sim-q-${q.idx}-text`} />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-white/60 bouncy cursor-pointer glass rounded-xl px-3 py-2" data-testid={`sim-q-${q.idx}-photo-label`}>
                    <Camera size={16} style={{ color: "var(--pink)" }} /> {tr("Allega foto della soluzione")}
                    <input type="file" accept="image/*" className="hidden" data-testid={`sim-q-${q.idx}-photo`}
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => setAnswers({ ...answers, [q.idx]: { ...answers[q.idx], image: reader.result } });
                        reader.readAsDataURL(f);
                      }} />
                  </label>
                  {answers[q.idx]?.image && (
                    <div className="flex items-center gap-2">
                      <img src={answers[q.idx].image} alt="risposta" className="h-12 rounded-lg" />
                      <button onClick={() => setAnswers({ ...answers, [q.idx]: { ...answers[q.idx], image: null } })} className="text-white/50 hover:text-red-400"><X size={16} /></button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <button onClick={() => submit.current()} disabled={submitting} className="w-full py-4 rounded-2xl btn-neon font-heading font-bold bouncy disabled:opacity-60" data-testid="sim-submit-btn">
          {submitting ? tr("Valutazione in corso...") : tr("Consegna e valuta")}
        </button>
      </div>
    </div>
  );
}
